import React from 'react';
import { SectorOverviewPanel } from '../components/panels/sectors/SectorOverviewPanel';
import { FintechPanel } from '../components/panels/sectors/FintechPanel';
import { AIPanel } from '../components/panels/sectors/AIPanel';
import { Web3Panel } from '../components/panels/sectors/Web3Panel';
import { EcommercePanel } from '../components/panels/sectors/EcommercePanel';
import { ProjectionsPanel } from '../components/panels/sectors/ProjectionsPanel';
import { usePanelManager, PanelConfig } from '../hooks/usePanelManager';
import { MinimizedBar } from '../components/ui/MinimizedBar';

// Panel configuration
const panelConfig: PanelConfig[] = [
  { id: 'overview', title: 'Sector Overview', row: 1, colSpan: 4 },
  { id: 'fintech', title: 'FinTech', row: 2, colSpan: 2 },
  { id: 'ai', title: 'AI', row: 2, colSpan: 2 },
  { id: 'web3', title: 'Web3', row: 3, colSpan: 2 },
  { id: 'ecommerce', title: 'E-Commerce', row: 3, colSpan: 2 },
  { id: 'projections', title: 'Projections', row: 4, colSpan: 4 },
];

// Panel component map
const panelComponents: Record<string, React.FC<{ isMinimized?: boolean; onMinimize?: () => void }>> = {
  overview: SectorOverviewPanel,
  fintech: FintechPanel,
  ai: AIPanel,
  web3: Web3Panel,
  ecommerce: EcommercePanel,
  projections: ProjectionsPanel,
};

const SectorsView: React.FC = () => {
  const {
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  } = usePanelManager(panelConfig);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <MinimizedBar panels={minimizedPanelsList} onRestore={handleRestore} />

      <div className="flex-1 flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 md:grid-rows-[auto_1fr_1fr_auto] gap-px p-px bg-terminal-border overflow-y-auto md:overflow-hidden">
        {/* Row 1 - Sector Overview */}
        {getOrderedPanels(1).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[150px] sm:min-h-[180px] md:min-h-0 col-span-full h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 2 - FinTech & AI */}
        {getOrderedPanels(2).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 3 - Web3 & E-Commerce */}
        {getOrderedPanels(3).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 4 - Projections */}
        {getOrderedPanels(4).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[150px] sm:min-h-[180px] md:min-h-0 col-span-full h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectorsView;
